import { rem, useIsFeatureEnabled } from "@innovyze/stylovyze";
import { CardActionArea, CardContent, Grid, Typography } from "@mui/material";
import { TFunc } from '../../i18n/types';
import { useGlobalization } from '../../i18n/useGlobalization';
import { FullPage, FullPageWrapper } from "@Utils/styles";
import React from "react";
import { useHistory } from "react-router-dom";
import { LauncherCardWrapper } from "./Launcher.style";
import RiskIconLarge from "@Components/RiskIconLarge";
import RehabIconLarge from "@Components/RehabIconLarge";
import VenusIconLarge from "@Components/VenusIconLarge";

export enum LauncherPage {
    Analysis = 0,
}

export interface LauncherProps {
	page: LauncherPage;
}

enum LauncherButton {
    Venus,
    Risk,
    Rehab,
}

const getLauncherTitle = (t: TFunc, page: LauncherPage): string => {
    switch (page) {
        case LauncherPage.Analysis:
             return t('Analysis');
        default:
            return '';
    }
};

const onLauncherButtonClick = (launcherButton: LauncherButton, history: any) => {
    switch (launcherButton) {
        case LauncherButton.Risk: 
            history.push('/risk');
        break;
        case LauncherButton.Rehab: 
            history.push('/rehab');
        break;
        case LauncherButton.Venus:
            history.push('/deterioration');
        break;
    }
}

const getLauncherTile = (title: string, text: string, icon: React.ReactNode, launcherButton: LauncherButton, history: any, dataCY: string) => {
    return (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
            <LauncherCardWrapper>
                <CardActionArea
                    sx={{
                        height: '100%',
                        '&[hover-card]': {
                            '&:hover': {
                                backgroundColor:
                                    'action.selectedHover',
                            },
                        },
                    }}>
                        <CardContent style={{ height: '100%' }} data-cy={dataCY}>
                            <div
                                role="button"
                                onClick={() => onLauncherButtonClick(launcherButton, history)}
                                onKeyDown={() => onLauncherButtonClick(launcherButton, history)}
                                tabIndex={0}
                                className="card">
                                <div className="card__icon">
                                    { icon }
                                </div>
                                <div className="card__note">
                                    <Typography gutterBottom variant="h6">
                                        {title}
                                    </Typography>
                                    <Typography gutterBottom variant="subtitle1">
                                        {text}
                                    </Typography>
                                </div>
                            </div>
                        </CardContent>
                    </CardActionArea>
                </LauncherCardWrapper>
        </Grid>
    );
}

const getLauncher = (t: TFunc, page: LauncherPage, history: any, visibility: boolean[]) => {
    switch (page) {
        case LauncherPage.Analysis:
            return (
                <>
                    {visibility[0] ? 
                        getLauncherTile(t('Risk Models'), 
                            t('Identify high-risk assets to help you prioritize maintenance and reduce failures'), 
                            <RiskIconLarge />, 
                            LauncherButton.Risk, 
                            history, 
                            'launcher-button-risk') 
                        : []}
                    {visibility[1] ? 
                        getLauncherTile(t('Deterioration Models'), 
                            t('Predict asset failure using your own failure data or industry standard curves'), 
                            <VenusIconLarge />, 
                            LauncherButton.Venus, 
                            history, 
                            'launcher-button-deterioration') 
                        : []}
                    {visibility[2] ? 
                        getLauncherTile(t('Rehab Decision Trees'), 
                            t('Plan effective rehabilitation strategies based on asset condition and risk'), 
                            <RehabIconLarge />, 
                            LauncherButton.Rehab, 
                            history, 
                            'launcher-button-rehab')
                        : []}
                </>
            );
        default:
            return [];
    }
};


export const Launcher = ({ page }: LauncherProps) => {
    const { t } = useGlobalization();
    const history = useHistory();
    
	const enableVenus = !!useIsFeatureEnabled('info-360-am-venus');
    const enableRisk = !!useIsFeatureEnabled('info-360-am-risk');
    const enableRehab = !!useIsFeatureEnabled('info-360-asset-rehab');

    const visibility: boolean[] = [];

    switch (page) {
        case LauncherPage.Analysis:
            visibility.push(enableRisk, enableVenus, enableRehab);
        break;
    }

	return (
		<FullPageWrapper
			title={ getLauncherTitle(t, page) }
			applyPadding={true}
			headerUnderline 
        >
       		<FullPage>
                <Grid container spacing={2} direction="row" style={{ paddingTop: '2rem' }} >
                    { getLauncher(t, page, history, visibility) }
                </Grid>
            </FullPage>
        </FullPageWrapper>
	);    
}